import {
  Tooltip,
  Box,
  Typography,
  Popover,
  TextField,
  Badge,
  FormControlLabel,
  Switch,
  Grid,
  Toolbar,
  Button,
  IconButton,
  FormGroup,
  Paper,
} from '@mui/material';
import moment from 'moment';
import { Close, Event, DateRange } from '@mui/icons-material';
import { isSameDay, isWithinInterval, isAfter } from 'date-fns';
import {
  LocalizationProvider,
  StaticDatePicker,
  StaticDateTimePicker,
  PickersDayProps as MuiPickersDayProps,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import * as React from 'react';
import { useItemColors } from '../../utils/item.utils';
import { PickersDay } from '../date-ranges-input';
import { RangeItemContainer } from '../date-ranges-input/RangeItemContainer';
import { hasValidUSTime } from '../../utils';
import { ItemDate } from './ItemDateCollapse';
import { ElevatedBadge } from './ElevatedBadge';

interface DateValue {
  start?: Date;
  end?: Date;
}

export interface DateChipProps {
  value: DateValue;
  tooltip?: any;
  backgroundColor?: string;
  onDateChange?: Function; //(newValue: any) => void;
  disabled?: boolean;
  range?: boolean;
  time?: boolean;
  compact?: boolean;
  append?: string;
  badge?: number;
  onBadgeClick?: Function;
  icon?: any;
  date?: ItemDate;
  visibility?: string;
  elevation?: number;
  complete?: boolean;
}

export const DateChip = React.forwardRef(
  (
    {
      value,
      tooltip,
      backgroundColor,
      onDateChange = () => {},
      disabled,
      range = true,
      time = true,
      compact,
      badge,
      onBadgeClick = () => {},
      append,
      icon,
      date,
      visibility,
      elevation,
      complete,
    }: DateChipProps,
    ref
  ) => {
    const numDays =
      date?.end && moment(date?.end).isValid()
        ? Math.abs(moment(date?.end).diff(moment.now(), 'days'))
        : 0;
    const [anchorEl, setAnchorEl] =
      React.useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setTempDateValue(dateValue);
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };

    const zeroTime = (end?: Date) => !hasValidUSTime(end);

    const getLabel = ({ start, end }: DateValue) => {
      const isWholeDay = !!start || zeroTime(end);

      const numDays = isWholeDay
        ? moment(end).endOf('day').diff(moment().startOf('day'), 'days')
        : moment(end).startOf('day').diff(moment().startOf('day'), 'days');
      const numHours = isWholeDay
        ? moment(end).endOf('day').diff(moment.now(), 'hours')
        : moment(end).diff(moment.now(), 'hours');
      return !!start
        ? `${start ? moment(start).format('MM/DD') : ''} - ${
            end ? moment(end).format('MM/DD') : ''
          }`
        : !!end
        ? `${
            numDays < 0
              ? `${Math.abs(numDays)}d Past Due`
              : numDays <= 14
              ? numDays < 1
                ? numHours < 0
                  ? `${Math.abs(numHours)}h Past Due`
                  : `${numHours}h Left`
                : `${numDays}d Left${
                    !isWholeDay ? ` at ${moment(end).format('h:mm a')}` : ''
                  }`
              : moment(end).format(
                  !isWholeDay ? 'MMM D YYYY h:mm a' : 'MMM D YYYY'
                )
          }`
        : undefined;
    };

    const [dateValue, setDateValue] = React.useState<DateValue>(
      value || {
        start: null,
        end: null,
      }
    );
    const [tempDateValue, setTempDateValue] = React.useState<DateValue>(
      value || {
        start: null,
        end: null,
      }
    );
    const [label, setLabel] = React.useState<string | undefined>(
      getLabel(value)
    );
    const [isRange, setIsRange] = React.useState<boolean>(
      !!value?.start && range
    );
    const [isTime, setIsTime] = React.useState<boolean>(
      !!!value?.start && !!value?.end && zeroTime(value?.end)
    );
    const { start, end } = tempDateValue;
    const handleChange = (date: any) => {
      const newValue =
        date && moment(date).isValid()
          ? isTime
            ? moment(date).toDate()
            : moment(date).startOf('day').toDate()
          : null;
      if (newValue) {
        if (isRange) {
          if (start && !end && newValue > start) {
            setTempDateValue(prev => ({
              ...prev,
              end: newValue,
            }));
          } else {
            setTempDateValue(prev => {
              if (prev.start && isSameDay(prev.start, newValue)) {
                return { start: undefined, end: undefined };
              } else {
                return { start: newValue, end: undefined };
              }
            });
          }
        } else {
          setTempDateValue(prev => {
            if (prev.end && isSameDay(prev.end, newValue) && !isTime) {
              return { start: undefined, end: undefined };
            } else {
              return {
                start: undefined,
                end: newValue,
              };
            }
          });
        }
      }
    };

    React.useEffect(() => {
      const fixedValue = {
        start: value?.start,
        end: hasValidUSTime(value?.end)
          ? value?.end
          : value?.end && moment(value?.end).startOf('day').toDate(),
      };
      setDateValue(fixedValue);
      setLabel(getLabel(fixedValue));
      setIsRange(!!value?.start && range);
      setIsTime(!value?.start && hasValidUSTime(value?.end));
    }, [value]);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const DateWrapper = ({ children = null } = {} as any) => {
      if (tooltip) {
        return (
          <Tooltip arrow title={tooltip}>
            {children}
          </Tooltip>
        );
      } else {
        return children;
      }
    };
    const {
      datechip: { border: borderColor, background: bgColor, font: fontColor },
    } = useItemColors({
      date: dateValue?.end || dateValue?.start,
      complete: complete,
    });
    const renderDay = (
      date: Date,
      selectedDates: Array<Date | null>,
      pickersDayProps: MuiPickersDayProps<Date>
    ) => {
      const dayIsBetween =
        start && end && isAfter(end, start)
          ? isWithinInterval(date, { start, end })
          : false;
      const isFirstDay = start && isSameDay(date, start) ? true : false;
      const isLastDay = end && isSameDay(date, end) ? true : false;
      return (
        <Badge
          key={pickersDayProps?.day?.toString()}
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            isFirstDay && isRange ? (
              <Typography
                variant="inherit"
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: theme => theme.palette.secondary.main,
                  backgroundColor: 'white',
                  border: theme => `1px solid ${theme.palette.secondary.main}`,
                  px: '2px',
                  // py: compact ? 0 : '1px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                }}
              >
                START
              </Typography>
            ) : isLastDay && isRange ? (
              <Typography
                variant="inherit"
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: theme => theme.palette.secondary.main,
                  backgroundColor: 'white',
                  border: theme => `1px solid ${theme.palette.secondary.main}`,
                  px: '2px',
                  // py: compact ? 0 : '1px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                }}
              >
                END
              </Typography>
            ) : pickersDayProps?.today ? (
              <Typography
                variant="inherit"
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: theme => theme.palette.secondary.main,
                  backgroundColor: 'white',
                  border: theme => `1px solid ${theme.palette.secondary.main}`,
                  px: '2px',
                  // py: compact ? 0 : '1px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                }}
              >
                TODAY
              </Typography>
            ) : pickersDayProps?.selected || isLastDay ? (
              <Typography
                variant="inherit"
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: theme => theme.palette.secondary.main,
                  backgroundColor: 'white',
                  border: theme => `1px solid ${theme.palette.secondary.main}`,
                  px: '2px',
                  // py: compact ? 0 : '1px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                }}
              >
                {'DUE DATE'}
              </Typography>
            ) : undefined
          }
        >
          <PickersDay
            {...pickersDayProps}
            selected={pickersDayProps?.selected || isFirstDay || isLastDay}
            onDaySelect={(day: Date, isFinish: any) => {
              if (end && isSameDay(end, day)) {
                setTempDateValue({ start: undefined, end: undefined });
              } else {
                pickersDayProps.onDaySelect(day, isFinish);
              }
            }}
            dayIsBetween={dayIsBetween}
            isFirstDay={isFirstDay}
            isLastDay={isLastDay}
          />
        </Badge>
      );
    };
    return (
      <Box>
        <ElevatedBadge
          badgeContent={
            badge ? (
              <Tooltip title="Click to see additional dates">
                <Box
                  onClick={event => {
                    event.stopPropagation();
                    event.preventDefault();
                    onBadgeClick();
                  }}
                >
                  +{badge}
                </Box>
              </Tooltip>
            ) : (
              0
            )
          }
          color="secondary"
        >
          <Box
            ref={ref as any}
            onClick={(event: any) => {
              event.preventDefault();
              event.stopPropagation();
              if (!disabled) {
                handleClick(event);
              }
            }}
            // sx={{
            //   border: borderColor ? `1px solid ${borderColor}` : undefined,
            //   borderRadius: '20px',
            //   color: fontColor,
            //   px: 1,
            //   py: compact ? 0 : 0.5,
            //   whiteSpace: 'nowrap',
            //   display: 'flex',
            //   alignItems: 'center',
            //   flexWrap: 'nowrap',
            //   backgroundColor:
            //     bgColor === 'transparent'
            //       ? backgroundColor || bgColor
            //       : bgColor,
            // }}
            sx={{
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'nowrap',
              cursor: disabled ? 'default' : 'pointer',
            }}
          >
            <DateWrapper>
              <Paper
                elevation={!!label ? elevation : 0}
                sx={{
                  border: borderColor ? `1px solid ${borderColor}` : undefined,
                  borderRadius: '20px',
                  color: fontColor,
                  px: 1,
                  py: compact ? 0 : 0.5,
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  backgroundColor:
                    bgColor === 'transparent'
                      ? backgroundColor || bgColor
                      : bgColor,
                  width: 'fit-content',
                  visibility: visibility ? 'hidden' : undefined,
                }}
                // sx={{
                //   whiteSpace: 'nowrap',
                //   display: 'flex',
                //   alignItems: 'center',
                //   flexWrap: 'nowrap',
                // }}
              >
                {icon ? (
                  icon
                ) : isRange ? (
                  <DateRange
                    sx={{
                      color: fontColor,
                      fontSize: 15,
                      lineHeight: 15,
                      // py: 0,
                      my: 0,
                    }}
                  />
                ) : (
                  <Event
                    sx={{
                      color: fontColor,
                      fontSize: 15,
                      lineHeight: 15,
                      // py: 0,
                      my: 0,
                    }}
                  />
                )}
                {label && label !== '' && (
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {label}
                  </Typography>
                )}
              </Paper>
            </DateWrapper>
            {append && (
              <Paper
                elevation={elevation}
                sx={{
                  bgColor: 'white',
                  px: 1,
                  ml: 1,
                  py: compact ? 0 : 0.5,
                  border: theme => `1px solid ${theme.palette.secondary.main}`,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 400 }}>
                  {append}
                </Typography>
              </Paper>
            )}
          </Box>
        </ElevatedBadge>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={(event: any) => {
            event.preventDefault();
            event.stopPropagation();
            handleClose();
          }}
          onClick={(event: any) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {!isTime ? (
              <StaticDatePicker
                showToolbar={true}
                components={{
                  ActionBar: (props: any) => (
                    <Grid
                      container
                      spacing={0}
                      direction="row"
                      justifyContent="flex-end"
                      alignItems="center"
                      flexWrap={'nowrap'}
                      sx={{ mb: 2 }}
                    >
                      <Grid item>
                        <Button onClick={() => handleClose()}>Cancel</Button>
                      </Grid>
                      <Grid item>
                        <Button
                          onClick={() => {
                            setDateValue(tempDateValue);
                            onDateChange(tempDateValue);
                            setLabel(getLabel(tempDateValue));
                            handleClose();
                          }}
                        >
                          Ok
                        </Button>
                      </Grid>
                    </Grid>
                  ),
                }}
                toolbarTitle={
                  <Grid
                    container
                    spacing={0}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap={'nowrap'}
                    sx={{ width: '100%' }}
                  >
                    <Grid item sx={{ mr: isRange ? 2 : 3 }}>
                      {isRange ? 'Select Date Range' : 'Select Due Date'}
                    </Grid>

                    <Grid item>
                      <FormGroup>
                        {range && !(time && !isRange && tempDateValue.end) && (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isRange}
                                onClick={() => {
                                  setTempDateValue({
                                    start: undefined,
                                    end: undefined,
                                  });
                                  setIsRange(pv => !pv);
                                }}
                                size="small"
                                color="secondary"
                              />
                            }
                            label={
                              <Typography variant="caption">
                                ADD DATE RANGE
                              </Typography>
                            }
                          />
                        )}
                        {time && !isRange && tempDateValue.end && (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isTime}
                                onClick={() => {
                                  setTempDateValue(pv => ({
                                    start: undefined,
                                    end: moment(pv.end).startOf('day').toDate(),
                                  }));
                                  setIsTime(pv => !pv);
                                }}
                                size="small"
                                color="secondary"
                              />
                            }
                            label={
                              <Typography variant="caption">
                                ADD TIME
                              </Typography>
                            }
                          />
                        )}
                      </FormGroup>
                    </Grid>
                  </Grid>
                }
                value={
                  tempDateValue.end && moment(tempDateValue.end).isValid()
                    ? moment(tempDateValue.end).local()
                    : null
                }
                // onChange={value => handleChange(moment(value))}
                onChange={date => {
                  handleChange(date);
                }}
                onAccept={date => {
                  onDateChange(
                    date && moment(date).isValid()
                      ? isTime
                        ? moment(date).toDate()
                        : moment(date).startOf('day').toDate()
                      : null
                  );
                  handleClose();
                }}
                renderInput={params => {
                  return <TextField {...params} />;
                }}
                renderDay={renderDay}
                {...(isRange
                  ? {
                      ToolbarComponent: () => {
                        return (
                          <Toolbar
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              padding: '16px 24px',
                            }}
                          >
                            <Grid
                              container
                              spacing={0}
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              flexWrap={'nowrap'}
                              sx={{ width: '100%' }}
                            >
                              <Grid item sx={{ mr: isRange ? 2 : 8 }}>
                                <Typography
                                  variant="overline"
                                  sx={{
                                    color: theme =>
                                      theme.palette.text.secondary,
                                  }}
                                >
                                  {isRange
                                    ? 'Select Date Range'
                                    : 'Select Date'}
                                </Typography>
                              </Grid>
                              <Grid item>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isRange}
                                      onClick={() => {
                                        setTempDateValue({
                                          start: undefined,
                                          end: undefined,
                                        });
                                        setIsRange(pv => !pv);
                                      }}
                                      size="small"
                                      color="secondary"
                                    />
                                  }
                                  label={
                                    <Typography variant="caption">
                                      ADD DATE RANGE
                                    </Typography>
                                  }
                                />
                              </Grid>
                            </Grid>
                            <Box sx={{ width: '100%' }}>
                              <RangeItemContainer
                                action={
                                  <IconButton
                                    color="inherit"
                                    size="small"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setTempDateValue({
                                        start: undefined,
                                        end: undefined,
                                      });
                                    }}
                                  >
                                    <Close sx={{ fontSize: 'inherit' }} />
                                  </IconButton>
                                }
                              >
                                {`${
                                  tempDateValue.start &&
                                  moment(tempDateValue.start).isValid()
                                    ? moment(tempDateValue.start)
                                        .local()
                                        .format('MM/DD')
                                    : ''
                                } - ${
                                  tempDateValue.end &&
                                  moment(tempDateValue.end).isValid()
                                    ? moment(tempDateValue.end)
                                        .local()
                                        .format('MM/DD')
                                    : ''
                                }`}
                              </RangeItemContainer>
                            </Box>
                          </Toolbar>
                        );
                      },
                    }
                  : {})}
              />
            ) : (
              <StaticDateTimePicker
                showToolbar={true}
                displayStaticWrapperAs="mobile"
                components={{
                  ActionBar: (props: any) => (
                    <Grid
                      container
                      spacing={0}
                      direction="row"
                      justifyContent="flex-end"
                      alignItems="center"
                      flexWrap={'nowrap'}
                      sx={{ mb: 2 }}
                    >
                      <Grid item>
                        <Button onClick={() => handleClose()}>Cancel</Button>
                      </Grid>
                      <Grid item>
                        <Button
                          onClick={() => {
                            setDateValue(tempDateValue);
                            onDateChange(tempDateValue);
                            setLabel(getLabel(tempDateValue));
                            handleClose();
                          }}
                        >
                          Ok
                        </Button>
                      </Grid>
                    </Grid>
                  ),
                }}
                toolbarTitle={
                  <Grid
                    container
                    spacing={0}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap={'nowrap'}
                    sx={{ width: '100%' }}
                  >
                    <Grid item sx={{ mr: isRange ? 2 : 3 }}>
                      {isRange ? 'Select Date Range' : 'Select Due Date'}
                    </Grid>

                    <Grid item>
                      <FormGroup>
                        {range && !(time && !isRange && tempDateValue.end) && (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isRange}
                                onClick={() => {
                                  setTempDateValue({
                                    start: undefined,
                                    end: undefined,
                                  });
                                  setIsRange(pv => !pv);
                                }}
                                size="small"
                                color="secondary"
                              />
                            }
                            label={
                              <Typography variant="caption">
                                ADD DATE RANGE
                              </Typography>
                            }
                          />
                        )}
                        {time && !isRange && tempDateValue.end && (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isTime}
                                onClick={() => {
                                  setTempDateValue(pv => ({
                                    start: undefined,
                                    end: moment(pv.end).startOf('day').toDate(),
                                  }));
                                  setIsTime(pv => !pv);
                                }}
                                size="small"
                                color="secondary"
                              />
                            }
                            label={
                              <Typography variant="caption">
                                ADD TIME
                              </Typography>
                            }
                          />
                        )}
                      </FormGroup>
                    </Grid>
                  </Grid>
                }
                value={tempDateValue.end || null}
                onChange={date => {
                  handleChange(date);
                }}
                onAccept={date => {
                  onDateChange(
                    date && moment(date).isValid()
                      ? isTime
                        ? moment(date)
                        : moment(date).startOf('day')
                      : null
                  );
                  handleClose();
                }}
                renderInput={params => {
                  return <TextField {...params} />;
                }}
                renderDay={renderDay}
                {...(isRange
                  ? {
                      ToolbarComponent: () => {
                        return (
                          <Toolbar
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              padding: '16px 24px',
                            }}
                          >
                            <Grid
                              container
                              spacing={0}
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              flexWrap={'nowrap'}
                              sx={{ width: '100%' }}
                            >
                              <Grid item sx={{ mr: isRange ? 2 : 8 }}>
                                <Typography
                                  variant="overline"
                                  sx={{
                                    color: theme =>
                                      theme.palette.text.secondary,
                                  }}
                                >
                                  {isRange
                                    ? 'Select Date Range'
                                    : 'Select Date'}
                                </Typography>
                              </Grid>
                              <Grid item>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isRange}
                                      onClick={() => {
                                        setTempDateValue({
                                          start: undefined,
                                          end: undefined,
                                        });
                                        setIsRange(pv => !pv);
                                      }}
                                      size="small"
                                      color="secondary"
                                    />
                                  }
                                  label={
                                    <Typography variant="caption">
                                      ADD DATE RANGE
                                    </Typography>
                                  }
                                />
                              </Grid>
                            </Grid>
                            <Box sx={{ width: '100%' }}>
                              <RangeItemContainer
                                action={
                                  <IconButton
                                    color="inherit"
                                    size="small"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setTempDateValue({
                                        start: undefined,
                                        end: undefined,
                                      });
                                    }}
                                  >
                                    <Close sx={{ fontSize: 'inherit' }} />
                                  </IconButton>
                                }
                              >
                                {`${
                                  tempDateValue.start
                                    ? moment(tempDateValue.start).format(
                                        'MM/DD'
                                      )
                                    : ''
                                } - ${
                                  tempDateValue.end
                                    ? moment(tempDateValue.end).format('MM/DD')
                                    : ''
                                }`}
                              </RangeItemContainer>
                            </Box>
                          </Toolbar>
                        );
                      },
                    }
                  : {})}
              />
            )}
          </LocalizationProvider>
        </Popover>
      </Box>
    );
  }
);
